import { useState } from 'react'
import { DimensionScores } from '@/types'
import { DimensionBreakdown } from './DimensionBreakdown'
import { DimensionRadarChart } from '../charts/DimensionRadarChart'
import { DimensionBarChart } from '../charts/DimensionBarChart'
import { BarChart3, Radar, List } from 'lucide-react'
import { cn } from '@/utils/cn'

interface DimensionOverviewProps {
  dimensions: DimensionScores
  defaultView?: 'list' | 'bar' | 'radar'
  className?: string
}

type ViewType = 'list' | 'bar' | 'radar'

export function DimensionOverview({
  dimensions,
  defaultView = 'list',
  className
}: DimensionOverviewProps) {
  const [activeView, setActiveView] = useState<ViewType>(defaultView)

  const viewOptions = [
    { key: 'list' as ViewType, label: 'List View', icon: List },
    { key: 'bar' as ViewType, label: 'Bar Chart', icon: BarChart3 },
    { key: 'radar' as ViewType, label: 'Radar Chart', icon: Radar }
  ]

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm',
      className
    )}>
      {/* Header with View Toggle */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Dimension Analysis
          </h2>
          
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {viewOptions.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveView(key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  activeView === key
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
                title={label}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeView === 'list' && (
          <DimensionBreakdown
            dimensions={dimensions}
            showTrends={true}
            showDetails={true}
            interactive={true}
            className="border-0 shadow-none p-0 bg-transparent"
          />
        )}

        {activeView === 'bar' && (
          <div>
            <DimensionBarChart
              dimensions={dimensions}
              height={350}
              showWeights={true}
            />
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Dimension scores with relative weights
              </p>
            </div>
          </div>
        )}

        {activeView === 'radar' && (
          <div>
            <DimensionRadarChart
              dimensions={dimensions}
              height={350}
            />
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Radar view showing dimension balance
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="px-6 pb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {Math.max(
                dimensions.legal.score,
                dimensions.financial.score,
                dimensions.operational.score,
                dimensions.market.score
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Highest</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {Math.min(
                dimensions.legal.score,
                dimensions.financial.score,
                dimensions.operational.score,
                dimensions.market.score
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Lowest</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {(
                (dimensions.legal.score + dimensions.financial.score + 
                 dimensions.operational.score + dimensions.market.score) / 4
              ).toFixed(1)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Average</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {Object.values(dimensions).filter(d => d.trend === 'improving').length}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Improving</div>
          </div>
        </div>
      </div>
    </div>
  )
}